import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getWallpapers, deleteWallpaper, editWallpaper, getSizes, toAssetUrl } from '../../services/api';
import RetailerSidebar from './RetailerSidebar';

export default function Wallpapers() {
  const navigate = useNavigate();
  const [wallpapers, setWallpapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editFormData, setEditFormData] = useState({
    product_size_id: '',
    application_type: 'wall',
    surface_type: '',
    price: '',
    sku: '',
    pieces_per_box: '',
    price_unit: 'Box',
    coverage_area_per_box: '',
    coverage_area_unit: 'Per Sqmr',
    weight_per_box: '',
    weight_unit: 'Kg',
    thickness_mm: '',
    images: []
  });
  const [sizes, setSizes] = useState([]);
  const [editLoading, setEditLoading] = useState(false);

  const getImageSrc = (image) => toAssetUrl(image?.url || image);

  useEffect(() => {
    fetchWallpapers();
    fetchSizes();
  }, []);

  const fetchWallpapers = async () => {
    const token = localStorage.getItem('retailer_token');
    
    try {
      const response = await getWallpapers();
      if (response.success) {
        setWallpapers(response.products);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSizes = async () => {
    const token = localStorage.getItem('retailer_token');
    try {
      const response = await getSizes();
      if (response.success) {
        setSizes(response.sizes);
      }
    } catch (err) {
      console.error('Failed to fetch sizes:', err);
    }
  };

  const handleDelete = async (wallpaperId) => {
    try {
      const token = localStorage.getItem('retailer_token');
      const response = await deleteWallpaper(wallpaperId);
      if (response.success) {
        setWallpapers(wallpapers.filter(w => w.id !== wallpaperId));
        setDeleteConfirm(null);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (wallpaper) => {
    setEditFormData({
      product_size_id: wallpaper.product_size_id || '',
      application_type: wallpaper.application_type || 'wall',
      surface_type: wallpaper.surface_type || '',
      price: wallpaper.price || '',
      sku: wallpaper.sku || '',
      pieces_per_box: wallpaper.pieces_per_box || '',
      price_unit: wallpaper.price_unit || 'Box',
      coverage_area_per_box: wallpaper.coverage_area_per_box || '',
      coverage_area_unit: wallpaper.coverage_area_unit || 'Per Sqmr',
      weight_per_box: wallpaper.weight_per_box || '',
      weight_unit: wallpaper.weight_unit || 'Kg',
      thickness_mm: wallpaper.thickness_mm || '',
      images: []
    });
    setEditModal(wallpaper);
  };

  const handleEditChange = (e) => {
    const { name, value, files } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: name === 'images' ? files : value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setError('');

    const token = localStorage.getItem('retailer_token');
    

    try {
      const formDataToSend = new FormData();
      Object.keys(editFormData).forEach(key => {
        if (key === 'images') {
          for (let i = 0; i < editFormData.images.length; i++) {
            formDataToSend.append('images', editFormData.images[i]);
          }
        } else if (editFormData[key] !== '') {
          formDataToSend.append(key, editFormData[key]);
        }
      });

      const response = await editWallpaper(editModal.id, formDataToSend);
      if (response.success) {
        setEditModal(null);
        fetchWallpapers();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <RetailerSidebar />
      
      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Wallpapers</h1>
            <button
              onClick={() => navigate('/retailer/add-wallpaper')}
              className="bg-accent hover:bg-accent2 text-white font-semibold py-3 px-6 rounded-lg transition-all"
            >
              Add New Wallpaper
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading wallpapers...</p>
            </div>
          ) : wallpapers.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 shadow-sm text-center">
              <p className="text-gray-500 mb-4">No wallpapers added yet</p>
              <button
                onClick={() => navigate('/retailer/add-wallpaper')}
                className="bg-accent hover:bg-accent2 text-white font-semibold py-3 px-6 rounded-lg transition-all"
              >
                Add Your First Wallpaper
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Images</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">SKU</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Application Type</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Surface Type</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Price</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Price Unit</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Pieces per Box</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {wallpapers.map((wallpaper) => (
                    <tr key={wallpaper.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm">
                        {wallpaper.images && wallpaper.images.length > 0 ? (
                          <div className="flex gap-1">
                            {wallpaper.images.slice(0, 3).map((img, idx) => (
                              <img
                                key={idx}
                                src={getImageSrc(img)}
                                alt={img?.original_filename || 'Wallpaper'}
                                className="w-10 h-10 object-cover rounded border border-gray-200"
                              />
                            ))}
                            {wallpaper.images.length > 3 && (
                              <span className="text-xs text-gray-500 self-center">+{wallpaper.images.length - 3}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">{wallpaper.sku || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 capitalize">{wallpaper.application_type}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{wallpaper.surface_type}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">${wallpaper.price}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{wallpaper.price_unit}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{wallpaper.pieces_per_box || '-'}</td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(wallpaper)}
                            className="p-2 text-gray-600 hover:text-accent hover:bg-accent/10 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(wallpaper.id)}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {deleteConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Delete</h3>
                <p className="text-gray-600 mb-6">Are you sure you want to delete this wallpaper? This action cannot be undone.</p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDelete(deleteConfirm)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {editModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
              <div className="bg-white rounded-xl p-6 max-w-3xl w-full mx-4 my-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Edit Wallpaper</h3>
                  <button
                    onClick={() => setEditModal(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleEditSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Product Size</label>
                      <select
                        name="product_size_id"
                        value={editFormData.product_size_id}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                      >
                        <option value="">Select a size</option>
                        {sizes.map((size) => (
                          <option key={size.id} value={size.id}>
                            {size.name} ({size.width}x{size.height} {size.unit})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Application Type</label>
                      <select
                        name="application_type"
                        value={editFormData.application_type}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                      >
                        <option value="wall">Wall</option>
                        <option value="floor">Floor</option>
                        <option value="both">Both</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Surface Type</label>
                      <input
                        type="text"
                        name="surface_type"
                        value={editFormData.surface_type}
                        onChange={handleEditChange}
                        placeholder="e.g., vinyl, fabric"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
                      <input
                        type="number"
                        step="0.01"
                        name="price"
                        value={editFormData.price}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">SKU</label>
                      <input
                        type="text"
                        name="sku"
                        value={editFormData.sku}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pieces per Box</label>
                      <input
                        type="number"
                        name="pieces_per_box"
                        value={editFormData.pieces_per_box}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Price Unit</label>
                      <select
                        name="price_unit"
                        value={editFormData.price_unit}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                      >
                        <option value="Box">Box</option>
                        <option value="Roll">Roll</option>
                        <option value="Sqft">Sqft</option>
                        <option value="Sqmr">Sqmr</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Coverage Area</label>
                      <input
                        type="number"
                        step="0.01"
                        name="coverage_area_per_box"
                        value={editFormData.coverage_area_per_box}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Coverage Unit</label>
                      <select
                        name="coverage_area_unit"
                        value={editFormData.coverage_area_unit}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                      >
                        <option value="Per Sqmr">Per Sqmr</option>
                        <option value="Per Sqft">Per Sqft</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Weight</label>
                      <input
                        type="number"
                        step="0.01"
                        name="weight_per_box"
                        value={editFormData.weight_per_box}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Weight Unit</label>
                      <select
                        name="weight_unit"
                        value={editFormData.weight_unit}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                      >
                        <option value="Kg">Kg</option>
                        <option value="Lb">Lb</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Thickness (mm)</label>
                      <input
                        type="number"
                        step="0.1"
                        name="thickness_mm"
                        value={editFormData.thickness_mm}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Images (Optional)</label>
                    <input
                      type="file"
                      name="images"
                      onChange={handleEditChange}
                      multiple
                      accept="image/*"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">Upload new images to replace existing ones</p>
                  </div>

                  <div className="flex gap-3 justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => setEditModal(null)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={editLoading}
                      className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent2 transition-colors disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      {editLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
